import { cx } from '@emotion/css';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Divider,
  Input,
  Option,
  PhoneInput,
  Select,
  useMediaQuery,
} from '@sortlist-frontend/design-system/legacy';
import { Monitor } from '@sortlist-frontend/mlm';
import { GooglePlaceAutocompleteType } from '@sortlist-frontend/shared-components';
import { useTrack } from '@sortlist-frontend/tracking';
import { Trans, useTranslation } from '@sortlist-frontend/translation/ssr';
import { useIntercom } from '@sortlist-frontend/user-tools';
import { HttpUnprocessableEntity, isNonEmptyString, isString } from '@sortlist-frontend/utils';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { getSelectStyles } from '_components/styled/shared';
import { usePublicAppContext } from '_core/context/public-app-context';
import { agencyClaimRepo, Language } from '_core/repos/agency-claim.repo';
import { AgencyWithRelations } from '_core/repos/agency.repo';
import { useLanguages } from '_core/repos/languages.repo';
import { AgencyClaimFormValues, buildValidationSchema } from '_features/agency/claim/agency-claim-validation';
import { agencyClaimConfig } from '_features/agency/claim/agency-claim.config';
import { LocationSelect } from '_features/agency/claim/components/LocationSelect';
import { Services } from '_features/agency/claim/components/Services/Services';
import { transformServiceBudgetIfNull } from '_features/agency/claim/utils/service-budget.utils';
import { Service } from '_types/generic';

import { useVerifyDuplicateAgency } from '../../hooks/useVerifyDuplicateAgency';
import { ForbiddenInfoWarning } from '../ForbiddenInfoWarning';
import { FormSkeleton } from './skeleton';
import { getServerErrors } from './utils';

type Props = {
  agencyUuid?: string;
  claimSessionId: string;
  agencyData?: AgencyWithRelations;
  sessionData?: Partial<AgencyClaimFormValues>;
  agencyNameQuery?: string;
  lang: string;
};

const defaultValues: AgencyClaimFormValues = {
  agencyUuid: '',
  agencyName: '',
  phone: '',
  website: '',
  email: '',
  officeAddress: {
    address: '',
    coordinates: {
      type: '',
      coordinates: [],
    },
    google_place_id: '',
    language: '',
    types: [],
    polygon: [],
  },
  languages: [],
  services: [],
  teamSize: 1,
};

type FieldError = {
  error: string;
  value: string;
};

type ErrorResponse = {
  code: string;
  errors: {
    email?: FieldError[];
    phone?: FieldError[];
    website?: FieldError[];
    generic?: string;
    [key: string]: FieldError[] | string | undefined;
  };
  error_id: string | null;
};

export const AgencyForm = (props: Props) => {
  const { lang, claimSessionId, agencyData, sessionData, agencyNameQuery } = props;
  const { t } = useTranslation(agencyClaimConfig.i18nNamespaces);
  const { domainInfo } = usePublicAppContext();
  const { media_legacy } = useMediaQuery();
  const { data: languages, isLoading: isLoadingLanguages } = useLanguages(lang);
  const { showIntercom } = useIntercom();
  const router = useRouter();
  const { track } = useTrack({ app: 'public', locale: router.locale });

  const validationSchema = buildValidationSchema(t);
  const iso = domainInfo?.getIso31661() ? domainInfo?.getIso31661()?.toLowerCase() : 'gb';

  const [selectedServices, setSelectedServices] = useState<Service[]>([]);
  const [ready, setReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { mutateAsync: verifyDuplicateAgency } = useVerifyDuplicateAgency();

  const { errors: serverErrors } = router.query;

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting, isValid, isSubmitSuccessful, submitCount },
    setError,
    clearErrors,
  } = useForm<AgencyClaimFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues,
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    shouldFocusError: false,
  });

  const SERVER_ERRORS = getServerErrors(t, showIntercom);

  const getServerError = () => {
    if (!isString(serverErrors)) return null;

    let parsedServerErrors: ErrorResponse | null = null;

    try {
      parsedServerErrors = JSON.parse(serverErrors);
    } catch {
      Monitor.captureMessage('Invalid serverErrors query param', { extra: { serverErrors } });
      return;
    }

    applyFieldErrors(parsedServerErrors?.errors ?? {});
  };

  const applyFieldErrors = (fieldErrors: Partial<Record<'email' | 'phone' | 'website', { error: string }[]>>) => {
    const { email, phone, website } = fieldErrors;

    const emailError = email?.[0]?.error;
    const phoneError = phone?.[0]?.error ?? '';
    const websiteError = website?.[0]?.error;

    if (emailError != null)
      setError('email', {
        type: 'manual',
        message: emailError,
      });
    if (websiteError != null)
      setError('website', {
        type: 'manual',
        message: websiteError,
      });
    if (['taken', 'invalid_phone'].includes(phoneError))
      setError('phone', {
        type: 'manual',
        message: phoneError,
      });
  };

  const onSubmit = async (formValues: AgencyClaimFormValues) => {
    setIsLoading(true);
    try {
      // Before anything, make sure the agency being submitted does not have a duplicate
      await verifyDuplicateAgency({
        agencySlug: agencyData?.slug,
        phone: formValues.phone,
        email: formValues.email,
        website: formValues.website,
        claimSessionId,
      });
    } catch (e) {
      setIsLoading(false);
      return;
    }

    const formValuesCopy = JSON.parse(JSON.stringify(formValues));
    // Server needs phone number to be an object
    if (formValuesCopy?.phone) {
      const phoneNumberObject = { number: formValuesCopy?.phone, countryCode: '', input: '' };
      formValuesCopy.phone = phoneNumberObject;
    }

    try {
      const response = await agencyClaimRepo.saveAgencyClaimForm(claimSessionId, formValuesCopy, {
        locale: lang,
      });
      const { redirectTo } = response.data;

      if (agencyData != null) {
        track({
          slug: 'agencyClaimCompleted',
          data: {
            agencyId: agencyData.uuid,
            agencySlug: agencyData.slug,
          },
        });
      }

      window.location = redirectTo;
    } catch (e) {
      if (e instanceof HttpUnprocessableEntity) {
        const fieldErrors = e.getCoreApiErrorPayload()?.errors;
        if (fieldErrors != null) applyFieldErrors(fieldErrors);
      } else {
        try {
          Monitor.captureException(e);
        } catch (error) {}
      }
    }
    setIsLoading(false);
  };

  const mapLanguagesToOptions = (languages: Language[]) =>
    languages.reduce((memo, item) => {
      memo.push({ value: item.code, label: item.name });
      return memo;
    }, [] as Option[]);

  const toFormValues = (lang: string, agencyData: AgencyWithRelations) => {
    const {
      slug,
      name,
      uuid,
      phone,
      languages: agencyDataLanguages,
      email,
      website,
      team_size,
      _relations: { offices, services },
    } = agencyData;

    const alteredServices = services.map((service) => ({
      ...service,
      skill_tags: [],
    }));
    const officeAddress =
      offices.length > 0
        ? {
            address: offices[0].address,
            coordinates: offices[0].coordinates,
            google_place_id: offices[0]?.google_place_id ?? undefined,
            language: lang,
          }
        : {};

    setSelectedServices(alteredServices as unknown as Service[]);

    const mappedLangs = mapLanguagesToOptions(languages?.filter((l) => agencyDataLanguages.includes(l.code)) || []);

    return {
      agencyUuid: uuid,
      agencyName: name,
      agencySlug: slug,
      languages: mappedLangs,
      email,
      officeAddress,
      phone,
      services: alteredServices,
      teamSize: team_size,
      website,
    };
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      getServerError();
    }, 1000);

    return () => clearTimeout(timer);
  }, [setError, serverErrors]);

  const getErrorMessage = (message: string | string[] | undefined, field: 'website' | 'phone' | 'email') => {
    if (!isString(message)) return;
    if (field in SERVER_ERRORS) {
      const fieldErrors = SERVER_ERRORS[field as keyof typeof SERVER_ERRORS];
      return fieldErrors[message as keyof typeof fieldErrors] ?? message;
    }
    return message;
  };

  useEffect(() => {
    (async () => {
      if (sessionData) {
        sessionData.services && setSelectedServices(sessionData.services as unknown as Service[]);
        return { ...sessionData };
      }

      if (agencyData != null) return toFormValues(lang, agencyData);

      return { ...defaultValues, agencyName: agencyNameQuery };
    })().then((res) => {
      const result = reset({
        ...res,
        // Phone number will come as an object we need to use only the string for the PhoneInput
        phone: (res.phone as unknown as { number: string })?.number,
      } as unknown as AgencyClaimFormValues);
      const { services } = res;
      if (services != null) {
        const alteredServices = (services as unknown as Service[]).map((service) => ({
          ...service,
          budget: transformServiceBudgetIfNull(service.budget),
        }));
        setSelectedServices(alteredServices);
      }
      setReady(true);
      return result;
    });
  }, []);

  if (!ready || isLoadingLanguages) return <FormSkeleton />;

  return (
    <div
      className={cx(
        media_legacy.gtXs ? 'shadow-lg bg-neutral-50 border border-secondary-300 rounded-lg' : 'bg-secondary-50',
      )}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <section>
          <header className="px-8 pt-16 pb-4">
            <h3 className="font-bold">{t('agency:claim.form.profile.title')}</h3>
            <p className="text-base text-secondary-500 mt-2">{t('agency:claim.form.profile.subtitle_V2')}</p>
            <input {...(register && register('agencyUuid'))} type="hidden" id="agencyUuid" className="input" />
          </header>
          <div>
            <section className="py-12">
              <header className="px-8">
                <h4 className="font-bold">About</h4>
              </header>
              <div id="name" className="px-8 mt-8 flex flex-col items-stretch justify-start">
                <label className="flex flex-row items-center justify-start font-medium text-sm" htmlFor="languages">
                  <span>{t('agency:claim.form.profile.agencyNameLabel_V2')}</span>
                  <ForbiddenInfoWarning />
                </label>
                <Input
                  error={!!errors.agencyName?.message}
                  errorMessage={errors.agencyName?.message}
                  id="agencyName"
                  maxLength={60}
                  placeholder={t('agency:claim.form.profile.agencyNamePlaceholder')}
                  {...(register && register('agencyName'))}
                  type="text"
                  className="mt-2"
                />
              </div>
              <div className="flex flex-col sm:flex-row px-4 mt-2">
                <div id="language" className="px-4 flex flex-col items-start justify-start flex-1">
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label className="font-medium text-sm" htmlFor="languages">
                    <Trans i18nKey={'agency:claim.form.profile.languagesLabel_V2'}>
                      <span>Languages spoken </span>
                      <span className="text-secondary-500 text-sm">optional</span>
                    </Trans>
                  </label>
                  <Controller
                    render={({ field: { onChange, ref, ...rest } }) => (
                      <Select
                        isMulti
                        className="mt-2 w-full"
                        styles={getSelectStyles(!!errors.languages)}
                        noOptionsMessage={() => t('agency:claim.form.selectNoOptionsLabel')}
                        placeholder={t('agency:claim.form.selectLabel')}
                        options={mapLanguagesToOptions(languages ?? [])}
                        error={!!errors.languages}
                        onChange={onChange}
                        ref={ref}
                        {...rest}
                        components={{
                          IndicatorSeparator: () => null,
                        }}
                      />
                    )}
                    control={control}
                    name="languages"
                    defaultValue={[]}
                  />
                  {errors.languages && <p className="mt-1 text-error text-sm">{(errors.languages as any).message}</p>}
                </div>
                <div id="team" className="px-4 max-sm:mt-6 flex flex-col items-start justify-start flex-1">
                  <label className="font-medium text-sm" htmlFor="teamSize">
                    {t('agency:claim.form.profile.teamSizeLabel')}
                  </label>
                  <Input
                    error={!!errors.teamSize?.message}
                    errorMessage={errors.teamSize?.message}
                    id="teamSize"
                    placeholder={t('agency:claim.form.profile.teamSizePlaceholder')}
                    {...(register && register('teamSize'))}
                    type="number"
                    className="w-full mt-2"
                  />
                </div>
              </div>
            </section>
            <Divider />
            <section className="py-12">
              <header className="px-8">
                <h4 className="font-bold">{t('agency:claim.form.contact.title')}</h4>
              </header>
              <div className="flex flex-col items-stretch justify-start px-4 mt-8">
                <div id="address" className="flex-1 px-4 flex flex-col justify-between items-start">
                  <div className="flex flex-col items-start justify-start mb-2">
                    <label className="text-sm font-medium" htmlFor="officeAddress">
                      {t('agency:claim.form.profile.addressLabel_V2')}
                    </label>
                    <p className="mt-2 text-sm text-secondary-500">
                      {t('agency:claim.form.profile.addressDescription')}
                    </p>
                  </div>
                  <Controller
                    name="officeAddress"
                    control={control}
                    render={({ field: { value, onChange } }) => {
                      // If no place_id is present do not set input value
                      const val = isNonEmptyString((value as any).google_place_id) ? value : null;
                      return (
                        <LocationSelect
                          lang={lang}
                          value={val}
                          onChange={(address: GooglePlaceAutocompleteType) => {
                            onChange({ ...address, language: lang });
                          }}
                          placeholder={t('agency:claim.form.profile.addressPlaceholder_V2')}
                          error={errors.officeAddress ? 'true' : undefined}
                        />
                      );
                    }}
                  />
                  {errors.officeAddress && (
                    <p className="mt-1 text-error text-sm">
                      {(errors.officeAddress as any)?.message ||
                        errors.officeAddress?.address?.message ||
                        errors.officeAddress?.google_place_id?.message ||
                        errors.officeAddress?.language?.message}
                    </p>
                  )}
                </div>
                <div id="phone" className="flex-1 px-4 mt-8 flex flex-col justify-between items-start">
                  <div className="flex flex-col items-start justify-start">
                    <label className="font-medium text-sm" htmlFor="phone">
                      {t('agency:claim.form.contact.phoneLabel_V2')}
                    </label>
                    <p className="mt-2 text-sm text-secondary-500">{t('agency:claim.form.contact.phoneDescription')}</p>
                  </div>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field: { onChange, value, name, ref } }) => {
                      return (
                        <PhoneInput
                          name={name}
                          ref={ref}
                          className={'mt-2 w-full'}
                          defaultValue={value}
                          defaultCountryFlag={iso as string}
                          error={getErrorMessage(errors.phone?.message, 'phone')}
                          onChange={(value, _iso) => {
                            onChange(value);
                            if (serverErrors != null && errors?.phone != null) {
                              clearErrors('phone');
                            }
                          }}
                        />
                      );
                    }}
                  />
                </div>
              </div>
              <div className="flex flex-col items-stretch justify-start px-4 mt-8">
                <div id="website" className="mx-4 flex-1 flex flex-col justify-between items-start">
                  <label className="font-medium text-sm" htmlFor="website">
                    {t('agency:claim.form.contact.websiteLabel_V2')}
                  </label>
                  <Input
                    type="text"
                    id="website"
                    error={!!errors.website?.message}
                    errorMessage={getErrorMessage(errors.website?.message, 'website')}
                    placeholder={t('agency:claim.form.contact.websitePlaceholder')}
                    {...(register && register('website'))}
                    className="w-full mt-2"
                  />
                </div>
                <div id="email" className="mx-4 mt-8 flex-1 flex flex-col justify-between items-start">
                  <div className="flex flex-col items-start justify-start">
                    <label className="font-medium text-sm" htmlFor="email">
                      {t('agency:claim.form.contact.emailLabel_V2')}
                    </label>
                    <p className="text-sm text-secondary-500 mt-2">{t('agency:claim.form.contact.emailDescription')}</p>
                  </div>
                  <Input
                    className="w-full mt-2"
                    {...(register && register('email'))}
                    type="text"
                    id="email"
                    name="email"
                    placeholder={t('agency:claim.form.contact.emailPlaceholder')}
                    error={!!errors.email?.message}
                    errorMessage={getErrorMessage(errors.email?.message, 'email')}
                    onChange={() => {
                      if (serverErrors != null && errors?.email != null) clearErrors('email');
                    }}
                  />
                </div>
              </div>
            </section>
            <Divider />
            <section className="pt-12">
              <header className="px-8">
                <h4 className="font-bold">{t('agency:claim.form.services.title')}</h4>
                <p className="text-sm text-secondary-500 mt-2">{t('agency:claim.form.services.subtitle')}</p>
              </header>
              <div className="mt-4">
                <Controller
                  name="services"
                  control={control}
                  defaultValue={[]}
                  render={({ field: { value, name, onChange, ...rest } }) => {
                    return (
                      <Services
                        locale={lang}
                        services={selectedServices}
                        onChange={(services) => {
                          onChange(services);
                        }}
                        error={(errors?.services as unknown as { message: string })?.message}
                      />
                    );
                  }}
                />
              </div>
            </section>
          </div>
        </section>
        <footer className="p-8 flex flex-row justify-end items-center">
          <div className="flex flex-row justify-end items-center">
            {serverErrors && (
              <p className="text-sm text-error mr-2 inline-block">{t('agency:claim.form.errors.genericError')}</p>
            )}
            {submitCount > 0 && !isValid && !(isSubmitting || isSubmitSuccessful) ? (
              <p className="text-sm text-error mr-2 inline-block">{t('agency:claim.form.errors.formError')}</p>
            ) : null}
            <Button
              size="sm"
              type="submit"
              buttonStyle="primary"
              buttonVariant="raised"
              label={t('agency:claim.form.continueButton')}
              loading={isLoading}
              disabled={isLoading}
              id="agency-form-submit-btn"
            />
          </div>
        </footer>
      </form>
    </div>
  );
};
